/**
 * 管理画面の Firebase クライアント(App / Auth / Firestore)。
 *
 * apps/web の `services/firebase/client.ts` と同じ流儀:
 *   - `initializeApp` は1回だけ、env はここ以外から読まない
 *   - エミュレータ接続は env の1フラグで分岐
 * 違いは「匿名サインインをしない」点(管理画面は必ず明示ログイン)と、
 * App Check を初期化しない点(env.ts のコメント参照)。
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  initializeAuth,
  type Auth,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import type { AdminEnv } from './env';

export interface AdminFirebase {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
}

export function createAdminFirebase(env: AdminEnv, name?: string): AdminFirebase {
  const app = initializeApp(
    {
      apiKey: env.apiKey,
      authDomain: env.authDomain,
      projectId: env.projectId,
      appId: env.appId,
    },
    name,
  );
  // 永続化は **localStorage に固定** する(既定の IndexedDB を使わない)。
  // 既定の indexedDBLocalPersistence は、タブが非表示のあいだ Chrome に
  // データベースを閉じられることがあり(「Database is closing/hidden」)、
  // その状態でログインすると原因の分かりにくい失敗になる。
  // 管理画面はセッションを1タブで使うだけなので localStorage で十分。
  const auth = initializeAuth(app, { persistence: browserLocalPersistence });
  const db = getFirestore(app);

  if (env.emulator) {
    const { host, authPort, firestorePort } = env.emulator;
    connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
    connectFirestoreEmulator(db, host, firestorePort);
  }

  return { app, auth, db };
}
